import { GetStaticPropsContext } from 'next';
import React, {
  ReactElement,
  useState,
  useCallback,
} from 'react';
import Opensea from '../../../icon/opensea-icon.svg';
import Etherscan from '../../../icon/etherscan.svg';

import TxLoaderModal, { TxLoaderState } from '@components/TxLoader';
import { gql } from '@apollo/client';
import { format, isValid } from 'date-fns';
import {
  GetArtistCollectionQuery,
  GetArtistCollectionQueryVariables,
  GetCollectionSlugsQuery,
} from 'generated/graphql';
import CollectionImages from '@components/CollectionImages';
import { ContentPadding } from '@components/ContentPadding';
import { getClient } from 'db/contentAPI';
import Layout from '@components/Layout';
import { Media } from '@components/Media';
import SeasonNumber from '@components/SeasonNumber';
import {
  Collection,
  ArtistCollection,
  getIsSaleLive,
  getMintActionForCollection,
  getMintByImageActionForCollection,
  getImagesFromCollection,
} from 'logic/collections';
import Link from 'next/link';
import { routes } from '@utils/routes';
import { MarkdownText } from '@components/MarkdownText';
import { CollectionImage } from 'logic/images';
import {
  useDataWithFallback,
  useQueryWithFallback,
} from 'hooks/useDataWithFallback';

export default function ArtistCollectionPage({
  cachedCollection,
  cachedIsSaleActive,
  cachedCollectionImages,
}: {
  cachedCollection: Collection;
  cachedCollectionImages: CollectionImage[];
  cachedIsSaleActive: boolean;
  preview: boolean;
}): ReactElement {
  const collection = useQueryWithFallback(
    queryArtistCollection,
    cachedCollection
  );
  const [txState, setTxState] = useState<TxLoaderState>({
    state: null,
    txHash: null,
  });
  const featuredImage = collection?.image?.url ?? '';
  const droptime = collection?.droptime ? new Date(collection?.droptime) : null;
  const onMint = getMintActionForCollection(collection);
  const onMintByImage = getMintByImageActionForCollection(
    collection,
    setTxState
  );
  const getImages = useCallback(
    () => getImagesFromCollection(collection),
    [collection]
  );
  const imgs = useDataWithFallback(getImages, cachedCollectionImages);
  const getSaleActiveForCollection = useCallback(
    () => getIsSaleLive(collection),
    [collection]
  );
  const isSaleActive = useDataWithFallback(
    getSaleActiveForCollection,
    cachedIsSaleActive
  );

  if (!collection) {
    return null;
  }

  return (
    <Layout
      head={{
        title: collection.projectName,
        image: featuredImage,
        description: collection.projectDescription,
      }}
    >
      {/* TODO: standardize mx-4 */}
      <ContentPadding>
        <div
          className="w-full grid mx-auto sm:grid-cols-2 mt-8 sm:mx-4 mb-16"
          key={collection.projectName}
        >
          <div className="flex flex-col">
            <Link
              href={routes.commissionTier(collection.season.commission.slug)}
            >
              <a className="font-semibold">
                {collection.season.commission?.name} /{' '}
                <SeasonNumber seasonNumber={collection.season.seasonNumber} />
              </a>
            </Link>
            <h3 className="text-left no-i18n text-5xl font-semibold mt-3 mb-4">
              <MarkdownText text={collection.projectName} />
            </h3>
            <p className="font-semibold text-3xl">{collection.name}</p>
            <p className="mt-6">
              <MarkdownText text={collection.projectDescription} />
            </p>
            {isSaleActive === false && (
              <p className="pt-4 font-semibold">
                {isValid(droptime)
                  ? `Claim starts: ${format(
                      droptime,
                      'MMMM do yyyy, hh:mm aaaa zzzz'
                    )}`
                  : 'Coming soon'}
              </p>
            )}
            {isSaleActive === true && (
              <p className="pt-4 font-semibold">
                <div>Currently Minting</div>
                {/* TODO: How to detect availability */}
                {/* <div className="font-normal">
                    Available: {imgs.length}/{imgs.length}
                  </div> */}
              </p>
            )}
            {/* Jan 1st, 2022, 9:00PM ET */}
            <div className="pt-4 text-gray-600">
              {/* TODO: fill in data */}
              <div className="flex w-full">
                {collection.etherscan && (
                  <a
                    href={collection.etherscan}
                    className="inline-flex hover:opacity-50"
                    target="_blank"
                    rel="noreferrer"
                  >
                    <Etherscan className="fill-current w-6 h-6 mr-2" /> View on
                    Etherscan
                  </a>
                )}
              </div>
              <div className="flex w-full space-x-6">
                {collection.opensea && (
                  <a
                    href={collection.opensea}
                    className="inline-flex hover:opacity-50"
                    target="_blank"
                    rel="noreferrer"
                  >
                    <Opensea className="fill-current w-6 h-6 mr-2" /> View on
                    Opensea
                  </a>
                )}
              </div>
            </div>
            {/* <p className="pt-4">Currently Minting</p>
            <p>Available: 440/440</p> */}
            {isSaleActive && onMint && (
              <div className="mb-4 mt-9 hover:opacity-70 cursor-pointer">
                <a
                  onClick={() => onMint(imgs[0].id, setTxState)}
                  className="font-medium text-xl border-solid border-2 border-black px-7 py-3"
                >
                  MINT
                </a>
              </div>
            )}
          </div>
          <div className="w-full sm:pl-32 relative">
            {featuredImage.length > 0 && (
              <Media
                className="object-contain self-center w-full"
                src={featuredImage}
                height="100%"
                width="100%"
                layout="responsive"
                alt={collection.name}
              />
            )}
          </div>
        </div>
        {isSaleActive && (
          <CollectionImages images={imgs} onMint={onMintByImage} />
        )}
      </ContentPadding>
      <TxLoaderModal txState={txState} setTxState={setTxState} />
    </Layout>
  );
}

const queryArtistCollection = gql`
  query GetArtistCollection($slug: String!, $preview: Boolean) {
    collectionCollection(where: { slug: $slug }, preview: $preview) {
      items {
        ...artistCollection
      }
    }
  }
  ${ArtistCollection}
`;

const getCollectionPaths = gql`
  query GetCollectionSlugs {
    collectionCollection {
      items {
        slug
      }
    }
  }
`;

export async function getStaticPaths() {
  const { data } = await getClient().query<GetCollectionSlugsQuery>({
    query: getCollectionPaths,
  });
  return {
    paths: data.collectionCollection.items.map((item) => ({
      params: { slug: item.slug },
    })),
    fallback: true,
  };
}

export async function getStaticProps({
  preview,
  previewData,
  params: { slug },
}: GetStaticPropsContext) {
  const { data, error } = await getClient(
    (previewData as { api: string })?.api
  ).query<GetArtistCollectionQuery, GetArtistCollectionQueryVariables>({
    query: queryArtistCollection,
    variables: {
      preview,
      slug: slug as string,
    },
  });

  if (error) {
    console.error(
      `Failed to fetch collection ${slug}, received ${JSON.stringify(error)}`
    );
  }

  const collection = data.collectionCollection.items?.[0];
  let isSaleActive = false;
  if (data.collectionCollection.items.length) {
    try {
      isSaleActive = await getIsSaleLive(data.collectionCollection.items?.[0]);
    } catch (e) {
      console.log('Failed to fetch sale active: ' + e);
    }
  }

  let cachedCollectionImages = [];
  try {
    cachedCollectionImages = await getImagesFromCollection(collection);
  } catch (e) {
    console.error('Failed to fetch collection images: ' + e);
  }

  return {
    props: {
      cachedCollection: collection,
      cachedCollectionImages,
      cachedIsSaleActive: isSaleActive ?? false,
      preview: preview ?? false,
    },
    revalidate: 120,
  };
}
